import React, { useEffect, useState } from 'react';
import { useEmailTemplateStore } from '../../../store/useEmailTemplate';
import './EmailTemplateEditor.css';

const EmailTemplateEditor: React.FC = () => {
  const {
    config,
    themes,
    franchises,
    previewHtml,
    loading,
    isAdmin,
    selectedFranchiseId,
    loadTemplateConfig,
    saveTemplateConfig,
    resetTemplateConfig,
    loadThemes,
    generatePreview,
    setSelectedFranchise,
    checkUserRole
  } = useEmailTemplateStore();

  // 로컬 상태 (실시간 미리보기용)
  const [localConfig, setLocalConfig] = useState({
    title: '',
    subtitle: '',
    themeId: 'purple-gradient',
    kakaoChannelUrl: '',
    senderName: ''
  });

  // 초기 로드
  useEffect(() => {
    const init = async () => {
      await checkUserRole();
      await loadThemes();
    };
    init();
  }, []);

  // config 변경시 로컬 상태 업데이트
  useEffect(() => {
    if (config) {
      setLocalConfig({
        title: config.title || '',
        subtitle: config.subtitle || '',
        themeId: config.themeId || 'purple-gradient',
        kakaoChannelUrl: config.kakaoChannelUrl || '',
        senderName: config.senderName || ''
      });
    }
  }, [config]);

  // 실시간 미리보기 (디바운싱)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedFranchiseId) {
        generatePreview(localConfig);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localConfig, selectedFranchiseId]);

  // 입력 핸들러
  const handleInputChange = (field: string, value: string) => {
    setLocalConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 저장 핸들러
  const handleSave = () => {
    // 제목과 부제목이 비어있으면 저장 방지
    if (!localConfig.title?.trim() || !localConfig.subtitle?.trim()) {
      alert('제목과 부제목은 필수 입력 항목입니다.\n초기화 하려면 초기화 버튼을 사용하세요.');
      return;
    }
    saveTemplateConfig(localConfig);
  };

  // 관리자용 가맹점 선택
  const handleFranchiseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const franchiseId = e.target.value;
    if (franchiseId) {
      setSelectedFranchise(franchiseId);
    }
  };

  if (loading && !previewHtml) {
    return <div className="loading-container">로딩중...</div>;
  }

  return (
    <div className="email-template-editor">
      {/* 메인 컨텐츠 */}
      <div className="editor-content">
        {/* 왼쪽: 설정 폼 */}
        <div className="editor-form">
          <div className="form-section">
            <h2>📝 템플릿 설정</h2>
            
            {/* 메일전송명 입력 */}
            <div className="form-group">
              <label>메일전송명 (최대 30자)</label>
              <input
                type="text"
                value={localConfig.senderName}
                onChange={(e) => handleInputChange('senderName', e.target.value)}
                maxLength={30}
                placeholder="예: 사주톡톡 알림"
                disabled={!selectedFranchiseId}
              />
              <span className="char-count">{localConfig.senderName?.length || 0}/30</span>
            </div>

            {/* 제목 입력 */}
            <div className="form-group">
              <label>제목 (최대 20자) <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                value={localConfig.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                maxLength={20}
                placeholder="예: 사주톡톡"
                disabled={!selectedFranchiseId}
                required
              />
              <span className="char-count">{localConfig.title.length}/20</span>
            </div>

            {/* 부제목 입력 */}
            <div className="form-group">
              <label>부제목 (최대 50자) <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                value={localConfig.subtitle}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                maxLength={50}
                placeholder="예: 당신의 운명을 읽어드립니다"
                disabled={!selectedFranchiseId}
                required
              />
              <span className="char-count">{localConfig.subtitle.length}/50</span>
            </div>

            {/* 테마 선택 */}
            <div className="form-group">
              <label>색상 테마</label>
              <div className="theme-palette">
                {themes.map(theme => (
                  <div
                    key={theme.id}
                    className={`theme-item ${localConfig.themeId === theme.id ? 'selected' : ''}`}
                    onClick={() => handleInputChange('themeId', theme.id)}
                    title={theme.name}
                  >
                    <div 
                      className="theme-preview"
                      style={{ background: theme.headerStyle }}
                    >
                      <span className="theme-icon">{theme.icon}</span>
                    </div>
                    <span className="theme-name">{theme.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 카카오톡 채널 URL 입력 */}
            <div className="form-group">
              <label>카카오톡 채널 URL (선택사항)</label>
              <input
                type="text"
                value={localConfig.kakaoChannelUrl}
                onChange={(e) => handleInputChange('kakaoChannelUrl', e.target.value)}
                placeholder="예: https://pf.kakao.com/_xxxxxj"
                disabled={!selectedFranchiseId}
              />
              <small style={{color: '#666', fontSize: '12px', display: 'block', marginTop: '5px'}}>
                카카오톡 채널 URL을 입력하면 이메일에 '카카오톡 채널 추가' 버튼이 표시됩니다.
              </small>
            </div>

            {/* 버튼 */}
            <div className="form-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={!selectedFranchiseId || loading}
              >
                {loading ? '저장중...' : '저장'}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={resetTemplateConfig}
                disabled={!selectedFranchiseId || loading}
              >
                초기화
              </button>
            </div>

            {/* 기본값 안내 */}
            {config?.isDefault && (
              <div className="info-box">
                ℹ️ 현재 기본값을 사용중입니다.
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽: 미리보기 */}
        <div className="editor-preview">
          <div className="preview-header">
            <h2>👁️ 미리보기</h2>
            {isAdmin && (
              <div className="franchise-selector">
                <label>가맹점 선택:</label>
                <select 
                  value={selectedFranchiseId} 
                  onChange={handleFranchiseChange}
                >
                  <option value="">선택하세요</option>
                  {franchises.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="preview-container">
            {selectedFranchiseId ? (
              <iframe
                srcDoc={previewHtml}
                title="이메일 미리보기"
                className="preview-iframe"
              />
            ) : (
              <div className="preview-placeholder">
                {isAdmin ? '가맹점을 선택하세요' : '로딩중...'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
